import {
  Camera,
  Euler,
  Matrix4,
  Object3D,
  Quaternion,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { environment } from '../../../environments/environment';
import {
  Point2D,
  Point3D,
} from '../../team-apis/otterdam/answers/shape-typings';
import { XYZWQuaternionTuple } from '../../team-apis/otterdam/calibration-data';
import { WXYZQuaternionTuple } from '../../team-apis/otterdam/locations-data';
import { Plane } from '../render/controls/side-view-resize-controls';
import {
  CUBE_MIN_LEGAL_SCALE,
  CuboidLocationState3D,
} from '../state/cuboid-location-state-3d';
import { mapPoint, roundValue } from './point-utils';

// NOTE: In standard camera format of up being Z
// FRONT/BACK is Y and TOP/BOTTOM is Z

export enum ThreeVertex {
  BackTopRight,
  BackBottomRight,
  FrontTopRight,
  FrontBottomRight,
  BackBottomLeft,
  BackTopLeft,
  FrontBottomLeft,
  FrontTopLeft,
}

export enum ThreeVertexForGeometry {
  BackTopRight = 0,
  BackBottomRight = 2,
  FrontTopRight = 1,
  FrontBottomRight = 4,
  BackBottomLeft = 6,
  BackTopLeft = 8,
  FrontBottomLeft = 9,
  FrontTopLeft = 10,
}

// Tent poles
export enum TentPoleVertex {
  FrontBottomRight,
  FrontTopRight,
  FrontBottomLeft,
  FrontTopLeft,
  BackBottomRight,
  BackTopRight,
  BackBottomLeft,
  BackTopLeft,
}

// Axis that is used for rotation reference when using task.data
export const REFERENCE_AXIS = [1, 0, 0] as const;

export function getLegalizedScale(scale: Point3D) {
  return mapPoint(scale, (value) => Math.max(CUBE_MIN_LEGAL_SCALE, value));
}

/*
 * Gets the average z coordinate from points in a position array
 */
export function getAverageZCoord(locations: Float32Array) {
  let sum = 0;
  const count = locations.length / 3;

  for (let i = 2; i < locations.length; i += 3) {
    sum += locations[i];
  }

  return roundValue(sum / count);
}

export function vectorToPoint(vector: Vector3) {
  return vector.toArray() as Point3D;
}

export function eulerToPoint(euler: Euler) {
  return euler.toArray().slice(0, 3) as Point3D;
}

export function pointToVector(point: Point3D) {
  return new Vector3(...point);
}

export function buildVectorRotatedBetweenSpaces(
  vector: Vector3,
  srcObject: Object3D,
  destObject: Object3D,
) {
  return buildVectorRotatedToObjectSpace(
    buildVectorRotatedToWorldSpace(vector, srcObject),
    destObject,
  );
}

export function buildVectorRotatedToWorldSpace(
  vector: Vector3,
  object: Object3D,
) {
  return vector
    .clone()
    .applyQuaternion(object.getWorldQuaternion(new Quaternion()));
}

export function buildVectorRotatedToObjectSpace(
  vector: Vector3,
  object: Object3D,
) {
  return vector
    .clone()
    .applyQuaternion(object.getWorldQuaternion(new Quaternion()).invert());
}

export function getMouseContainerPoint(
  event: MouseEvent,
  container: HTMLElement,
) {
  const rect = container.getBoundingClientRect();

  const containerPoint: Point2D = [
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    (-(event.clientY - rect.top) / rect.height) * 2 + 1,
  ];

  return containerPoint;
}

export function getMouseVector(event: MouseEvent, container: HTMLElement) {
  return new Vector2().fromArray(getMouseContainerPoint(event, container));
}

export function createRenderer(canvas?: HTMLCanvasElement) {
  const renderer = new WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    stencil: false,
    powerPreference: 'high-performance',
  });

  renderer.setClearColor(0xffffff, 0);

  // Performance optimization to avoid long calls to getProgramInfoLog,
  // should be disabled only in prod environments
  // https://threejs.org/docs/#api/en/renderers/WebGLRenderer.debug
  renderer.debug.checkShaderErrors = environment.checkShaderErrors;

  // Believe it or not, we didn't come up with a cleaner way to disable 3D
  // rendering in circle (which speeds up tests there significantly)
  if ((window as any).__karma__?.config.ursa?.disable3DRendering) {
    renderer.render = (scene, camera) => {
      // Since the camera is no longer in the scene, we have to update its
      // matrix world separately. This would have normally be done by the
      // render() call.
      camera.updateMatrixWorld();
      scene.updateMatrixWorld();
      callAfterRenderHooks(scene);
      updateModelViewMatrix(scene, camera);
    };
  }

  if (environment.name === 'test') {
    configureRendererErrorHandler(renderer);
  }

  return renderer;
}

function configureRendererErrorHandler(renderer: WebGLRenderer) {
  renderer.debug.onShaderError = (
    webglContext,
    _,
    vertexShader,
    fragmentShader,
  ) => {
    const vertexErrors = webglContext.getShaderInfoLog(vertexShader)?.trim();
    const fragmentErrors = webglContext
      .getShaderInfoLog(fragmentShader)
      ?.trim();

    throw new Error(
      `Shader error: \n
      vertex shader errors: ${vertexErrors}\n
      fragment shader errors: ${fragmentErrors}
      }`,
    );
  };
}

function callAfterRenderHooks(object: Object3D) {
  // HACK ALERT! We only use the afterrender hook in one place, and it doesn't
  // need any arguments. This hack is a for a performance improvement in tests.
  (object as any).onAfterRender();
  for (const child of object.children) {
    callAfterRenderHooks(child);
  }
}

function updateModelViewMatrix(object: Object3D, camera: Camera) {
  object.onBeforeRender = () => {
    object.modelViewMatrix.multiplyMatrices(
      camera.matrixWorldInverse,
      object.matrixWorld,
    );
  };
  for (const child of object.children) {
    updateModelViewMatrix(child, camera);
  }
}

export function getZInViewCoordinates(
  vertex: TentPoleVertex,
  cuboidPoints: Point3D[],
  plane: Plane,
) {
  const vector = pointToVector(cuboidPoints[vertex]);
  return plane.worldToLocal(vector).z;
}

export function buildMatrixWorldFromLocation({
  position,
  rotation,
  scale,
}: Pick<CuboidLocationState3D, 'position' | 'rotation' | 'scale'>) {
  const euler = new Euler().fromArray(rotation);
  const quaternion = new Quaternion().setFromEuler(euler);
  return new Matrix4().compose(
    pointToVector(position),
    quaternion,
    pointToVector(scale),
  );
}

export function eulerToWXYZQuaternion(
  x = 0,
  y = 0,
  z = 0,
): WXYZQuaternionTuple {
  const quaternion = new Quaternion().setFromEuler(new Euler(x, y, z));
  return [quaternion.w, quaternion.x, quaternion.y, quaternion.z];
}

export function eulerToXYZWQuaternion(
  x = 0,
  y = 0,
  z = 0,
): XYZWQuaternionTuple {
  const quaternion = new Quaternion().setFromEuler(new Euler(x, y, z));
  return [quaternion.x, quaternion.y, quaternion.z, quaternion.w];
}
